/**
 * StoryProgressionSystem - Manages story progression through collectibles
 * Tracks collected lore items, story completion, and unlocks
 */

import { loreItems, getLoreItemById, getLoreItemsForNight, getTotalLoreItemCount, type LoreItem } from '../data/loreItems';

export interface CollectedItem {
  itemId: string;
  nightCollected: number;
  timestamp: number;
}

export interface StoryProgress {
  collectedItems: CollectedItem[];
  nightsCompleted: number;
  totalCollectibles: number;
  secretsFound: number;
  storyPercentage: number;
}

export interface NightProgress {
  night: number;
  collectiblesAvailable: number;
  collectiblesFound: number;
  isComplete: boolean;
}

export class StoryProgressionSystem {
  private static instance: StoryProgressionSystem;

  // Collected items storage
  private collectedItems: Map<string, CollectedItem> = new Map();

  // Current night
  private currentNight: number = 1;

  // Highest night completed
  private nightsCompleted: number = 0;

  // Callbacks
  private onItemCollectedCallbacks: Array<(item: LoreItem, isNew: boolean) => void> = [];
  private onStoryMilestoneCallbacks: Array<(milestone: string, percentage: number) => void> = [];

  private constructor() {}

  public static getInstance(): StoryProgressionSystem {
    if (!StoryProgressionSystem.instance) {
      StoryProgressionSystem.instance = new StoryProgressionSystem();
    }
    return StoryProgressionSystem.instance;
  }

  /**
   * Collect a lore item
   */
  public collectItem(itemId: string): boolean {
    const loreItem = getLoreItemById(itemId);
    if (!loreItem) {
      console.error(`Lore item not found: ${itemId}`);
      return false;
    }

    const isNew = !this.collectedItems.has(itemId);

    if (isNew) {
      this.collectedItems.set(itemId, {
        itemId,
        nightCollected: this.currentNight,
        timestamp: Date.now(),
      });

      console.log(`📜 Collected lore item: ${loreItem.name}`);

      // Check for story milestones
      this.checkMilestones();
    }

    // Trigger callbacks
    this.onItemCollectedCallbacks.forEach(cb => cb(loreItem, isNew));

    return isNew;
  }

  /**
   * Check if an item has been collected
   */
  public hasCollected(itemId: string): boolean {
    return this.collectedItems.has(itemId);
  }

  /**
   * Get collected item data
   */
  public getCollectedItem(itemId: string): CollectedItem | undefined {
    return this.collectedItems.get(itemId);
  }

  /**
   * Get all collected items
   */
  public getAllCollectedItems(): CollectedItem[] {
    return Array.from(this.collectedItems.values());
  }

  /**
   * Get collected lore items with full data
   */
  public getCollectedLoreItems(): LoreItem[] {
    return Array.from(this.collectedItems.keys())
      .map(id => getLoreItemById(id))
      .filter((item): item is LoreItem => item !== undefined);
  }

  /**
   * Get story progress summary
   */
  public getProgress(): StoryProgress {
    const total = getTotalLoreItemCount();
    const collected = this.collectedItems.size;
    const secrets = this.getCollectedLoreItems().filter(item => item.isSecret).length;

    return {
      collectedItems: this.getAllCollectedItems(),
      nightsCompleted: this.nightsCompleted,
      totalCollectibles: total,
      secretsFound: secrets,
      storyPercentage: total > 0 ? Math.round((collected / total) * 100) : 0,
    };
  }

  /**
   * Get progress for a specific night
   */
  public getNightProgress(night: number): NightProgress {
    const available = loreItems.filter(item => item.nightFound === night);
    const found = available.filter(item => this.hasCollected(item.id));

    return {
      night,
      collectiblesAvailable: available.length,
      collectiblesFound: found.length,
      isComplete: found.length === available.length,
    };
  }

  /**
   * Get progress for all nights
   */
  public getAllNightProgress(): NightProgress[] {
    const progress: NightProgress[] = [];
    for (let i = 1; i <= 7; i++) {
      progress.push(this.getNightProgress(i));
    }
    return progress;
  }

  /**
   * Get lore items for viewing (includes collection status)
   */
  public getLoreItemsForViewing(night?: number): Array<LoreItem & { isCollected: boolean; collectedData?: CollectedItem }> {
    const items = night !== undefined ? getLoreItemsForNight(night) : loreItems;

    return items.map(item => ({
      ...item,
      isCollected: this.hasCollected(item.id),
      collectedData: this.getCollectedItem(item.id),
    }));
  }

  /**
   * Set current night
   */
  public setCurrentNight(night: number): void {
    this.currentNight = night;
  }

  /**
   * Mark a night as completed
   */
  public completeNight(night: number): void {
    if (night > this.nightsCompleted) {
      this.nightsCompleted = night;
      console.log(`Night ${night} completed!`);
      this.checkMilestones();
    }
  }

  /**
   * Get highest completed night
   */
  public getHighestCompletedNight(): number {
    return this.nightsCompleted;
  }

  /**
   * Check for story milestones
   */
  private checkMilestones(): void {
    const progress = this.getProgress();
    const percentage = progress.storyPercentage;

    // Milestone checks
    const milestones = [
      { threshold: 25, name: 'The Investigation Begins' },
      { threshold: 50, name: 'Uncovering the Truth' },
      { threshold: 75, name: 'The Horrible Secret' },
      { threshold: 100, name: 'Full Story Revealed' },
    ];

    for (const milestone of milestones) {
      if (percentage >= milestone.threshold) {
        // Only trigger once per milestone (would need persistent storage for real implementation)
        this.onStoryMilestoneCallbacks.forEach(cb => cb(milestone.name, percentage));
      }
    }
  }

  /**
   * Register callback for item collection
   */
  public onItemCollected(callback: (item: LoreItem, isNew: boolean) => void): void {
    this.onItemCollectedCallbacks.push(callback);
  }

  /**
   * Register callback for story milestones
   */
  public onStoryMilestone(callback: (milestone: string, percentage: number) => void): void {
    this.onStoryMilestoneCallbacks.push(callback);
  }

  /**
   * Export progress data (for save system)
   */
  public exportProgress(): {
    collectedItems: CollectedItem[];
    nightsCompleted: number;
  } {
    return {
      collectedItems: this.getAllCollectedItems(),
      nightsCompleted: this.nightsCompleted,
    };
  }

  /**
   * Import progress data (for load system)
   */
  public importProgress(data: {
    collectedItems: CollectedItem[];
    nightsCompleted: number;
  }): void {
    this.collectedItems.clear();
    data.collectedItems.forEach(item => {
      this.collectedItems.set(item.itemId, item);
    });
    this.nightsCompleted = data.nightsCompleted;
    console.log(`📜 Imported ${data.collectedItems.length} collected items, ${data.nightsCompleted} nights completed`);
  }

  /**
   * Reset all progress
   */
  public reset(): void {
    this.collectedItems.clear();
    this.nightsCompleted = 0;
    this.currentNight = 1;
    console.log('📜 Story progress reset');
  }

  /**
   * Get collectibles found in current session/night
   */
  public getCollectiblesFoundThisNight(): LoreItem[] {
    return this.getCollectedLoreItems().filter(item => {
      const collected = this.getCollectedItem(item.id);
      return collected && collected.nightCollected === this.currentNight;
    });
  }

  /**
   * Check if player has found all items in a category
   */
  public hasCategoryComplete(type: LoreItem['type']): boolean {
    const itemsOfType = loreItems.filter(item => item.type === type);
    return itemsOfType.every(item => this.hasCollected(item.id));
  }

  /**
   * Get story unlock status (what story elements are available)
   */
  public getStoryUnlocks(): {
    newspaperTrailComplete: boolean;
    audioLogsComplete: boolean;
    photosComplete: boolean;
    documentsComplete: boolean;
    fullStoryUnlocked: boolean;
  } {
    return {
      newspaperTrailComplete: this.hasCategoryComplete('newspaper'),
      audioLogsComplete: this.hasCategoryComplete('audio_log'),
      photosComplete: this.hasCategoryComplete('photo'),
      documentsComplete: this.hasCategoryComplete('document'),
      fullStoryUnlocked: this.getProgress().storyPercentage === 100,
    };
  }
}

// Export singleton instance
export const storyProgressionSystem = StoryProgressionSystem.getInstance();
